/**
 * admin-rpc-client
 *
 * Thin wrapper around GlobalClient.rawRpcCall for issuing dbaccess.v1.*
 * RPCs against the continuum-rpc mailbox. Used by the admin pages
 * (DirectoryPage, UncorrelatedCredentialsPage) to read and mutate rows
 * in continuum.usergroup / continuum.usercredentials / continuum.groupmembership.
 *
 * The firewall already grants ContinuumAdmin full access on these tables,
 * so no custom hermes endpoint is needed.
 */

import { uuidv7 } from "uuidv7";
import GlobalClient from "../hermesclient/client";
import { ContentType, RpcFrameType } from "../hermesclient/proto/wsmessages";
import { nameMappingService } from "./NameMappingService";
import { asLogicalMailbox, MailboxAddress } from "../hermesclient/types";

export type DbRow = Record<string, unknown>;

export interface QueryResponse {
  rows: DbRow[];
  rowCount: number;
}

// The shape the dbaccess.v1.Query RPC actually returns, before query()/queryFull()
// normalize it. All fields optional — an older gateway may omit rowCount/columns.
interface RawQueryResponse {
  rows?: DbRow[];
  rowCount?: number;
  transformedSql?: string;
  columns?: Array<{ name: string; sqlType?: string }>;
}

export interface MutationResponse {
  success: boolean;
  error?: { message: string; code?: string };
  /** rows an update or delete changed; protojson leaves out a 0 */
  rowsAffected?: number;
}

export interface CatalogResponse {
  tables: Array<{
    table_name: string;
    schema_name?: string;
    description?: string;
    primary_keys?: string[];
    access_modes?: string[];
    /** the table has a soft-delete column */
    soft_delete?: boolean;
    columns?: Array<{
      column_name: string;
      field_kind?: string;
      include_in_star?: boolean;
      metadata?: string;
      /** for field_kind "generated": what fills it, when, on which access modes */
      generate_expression?: string;
      generate_mode?: string;
      generate_on?: string[];
    }>;
    joins?: Array<{ join_name: string; to_table: string; join_expression?: string; join_type?: string }>;
  }>;
  /** Registered database_connection rows; the continuum database is the unnamed default. */
  databases?: Array<{ name: string; dialect?: string; description?: string }>;
}

class AdminRpcClient {
  private targetMailbox: MailboxAddress | null = null;

  private async getTargetMailbox(): Promise<MailboxAddress> {
    if (!this.targetMailbox) {
      // dbaccess.v1.* RPCs are served on the "db" logical mailbox.
      this.targetMailbox = await nameMappingService.resolve(
        asLogicalMailbox("db"),
      );
    }
    return this.targetMailbox;
  }

  private async rpcCall<TResp>(
    endPoint: string,
    request: Record<string, unknown>,
  ): Promise<TResp> {
    const client = GlobalClient.get();
    const targetMailbox = await this.getTargetMailbox();
    const requestBytes = new TextEncoder().encode(JSON.stringify(request));

    const rpcResponse = await client.rawRpcCall({
      to: targetMailbox,
      endPoint,
      body: requestBytes,
      contentType: ContentType.Json,
    });

    const frameType = rpcResponse.header?.rpcHeader?.frameType;
    if (frameType === RpcFrameType.ErrorResponse) {
      const errorInfo = rpcResponse.header?.rpcHeader?.errorInfo;
      const errorMessage =
        errorInfo?.message ||
        errorInfo?.errorNode?.frame?.message ||
        `RPC call to ${endPoint} failed`;
      throw new Error(errorMessage);
    }

    if (!rpcResponse.data || rpcResponse.data.length === 0) {
      return {} as TResp;
    }

    const responseText = new TextDecoder().decode(rpcResponse.data);
    return JSON.parse(responseText) as TResp;
  }

  /**
   * The firewall's catalog for THIS caller: tables with the caller's access
   * modes, calculated/generated fields, and auto-joins (no physical columns).
   * The same RPC the MCP sql_schema tool calls.
   */
  async listTables(database?: string): Promise<CatalogResponse> {
    const resp = await this.rpcCall<CatalogResponse>("dbaccess.v1.ListTables", database ? { database } : {});
    return { tables: resp.tables ?? [], databases: resp.databases ?? [] };
  }

  /**
   * dbaccess.v1.ApplyChanges: inserts, updates and deletes applied in ONE transaction,
   * all or nothing. `changes` is the request's own shape (see edit-plan.ts RowChange).
   * A failure names the change that failed; nothing was written.
   */
  async applyChanges(
    changes: unknown[],
    opts: { database?: string; noModel?: boolean; preview?: boolean } = {},
  ): Promise<{ success: boolean; applied: number; failedIndex: number; error?: string; statements: string[] }> {
    const resp = await this.rpcCall<{
      success?: boolean;
      applied?: number;
      failedIndex?: number;
      failed_index?: number;
      error?: { message?: string };
      statements?: string[];
    }>("dbaccess.v1.ApplyChanges", {
      database: opts.database ?? "",
      changes,
      no_model: Boolean(opts.noModel),
      preview: Boolean(opts.preview),
    });
    return {
      success: Boolean(resp.success),
      applied: resp.applied ?? 0,
      failedIndex: resp.failedIndex ?? resp.failed_index ?? -1,
      error: resp.error?.message,
      statements: resp.statements ?? [],
    };
  }

  /** database: a continuum.database_connection name; left out, the continuum database. */
  async query(sql: string, database?: string): Promise<QueryResponse> {
    const resp = await this.rpcCall<RawQueryResponse>("dbaccess.v1.Query", database ? { sql, database } : { sql });
    return {
      rows: resp.rows ?? [],
      rowCount: resp.rowCount ?? (resp.rows?.length ?? 0),
    };
  }

  /**
   * Like query(), but returns the full firewall response: transformedSql
   * (the statement AFTER ACL rewriting — macros expanded, tables resolved)
   * and the result column metadata. Used by expression validators that probe
   * `SELECT <expr> FROM <table> LIMIT 0` and show the transformed SQL.
   */
  async queryFull(sql: string): Promise<{
    rows: DbRow[];
    rowCount: number;
    transformedSql?: string;
    columns?: Array<{ name: string; sqlType?: string }>;
  }> {
    const resp = await this.rpcCall<RawQueryResponse>("dbaccess.v1.Query", { sql });
    return {
      rows: resp.rows ?? [],
      rowCount: resp.rowCount ?? (resp.rows?.length ?? 0),
      transformedSql: resp.transformedSql,
      columns: resp.columns,
    };
  }

  /** database: a continuum.database_connection name; left out, the continuum database. */
  async insert(table: string, values: DbRow, database?: string): Promise<MutationResponse> {
    return this.rpcCall<MutationResponse>("dbaccess.v1.Insert", {
      tableName: table,
      values,
      returnRow: false,
      ...(database ? { database } : {}),
    });
  }

  async update(
    table: string,
    key: DbRow,
    values: DbRow,
    database?: string,
  ): Promise<MutationResponse> {
    return this.rpcCall<MutationResponse>("dbaccess.v1.Update", {
      tableName: table,
      key,
      values,
      returnRow: false,
      ...(database ? { database } : {}),
    });
  }

  async delete(table: string, key: DbRow, database?: string): Promise<MutationResponse> {
    return this.rpcCall<MutationResponse>("dbaccess.v1.Delete", {
      tableName: table,
      key,
      ...(database ? { database } : {}),
    });
  }

  // upsert = insert-or-update keyed on keyColumns (the natural key, NOT the PK).
  // dbaccess.v1.Upsert is a lookup-then-insert/update (no ON CONFLICT): the
  // lookup matches keyColumns with NO status filter, so it reactivates a
  // soft-deleted row instead of colliding on the unique index. `values` must
  // include the keyColumns plus the data (and a fresh PK uid for the insert path
  // — the update path leaves the PK untouched).
  async upsert(
    table: string,
    keyColumns: string[],
    values: DbRow,
  ): Promise<MutationResponse> {
    return this.rpcCall<MutationResponse>("dbaccess.v1.Upsert", {
      tableName: table,
      keyColumns,
      values,
      returnRow: false,
    });
  }
}

export const adminRpc = new AdminRpcClient();

// ---------------------------------------------------------------------------
// Domain shapes the admin pages care about.

export interface UserGroupRow {
  uid: string;
  name: string;
  email: string;
  description: string;
  // handle: the person's short, permanent name (glen, robbi) that apps such as the ticket app
  // record them by. "" when none is set (groups, service accounts).
  handle: string;
  status: string;
  // show_as_user / show_as_group are display-lens calculations from the
  // qubes firewall model — the underlying model does NOT distinguish users
  // from groups; this is purely a UI fidelity hint.
  show_as_user: boolean;
  show_as_group: boolean;
  // Raw extra_data JSONB. The `show_as` override (when present) lives at
  // extra_data.show_as — "user" | "group" | "both" forces the lens
  // regardless of the natural calc; absent means "use the natural calc".
  extra_data: unknown;
}

// ShowAsOverride is the union of the four UI choices an operator can
// make for a row's display lens. `null` means "clear the override and
// fall back to the natural calc."
export type ShowAsOverride = null | "user" | "group" | "both";

export interface CredentialRow {
  uid: string;
  usergroupuid: string | null;
  provider: string;
  lookupcode: string;
  rawdata: string;
  extra_data: unknown;
  status: string;
}

export interface MembershipRow {
  uid: string;
  memberuid: string;
  groupuid: string;
  status: string;
}

function esc(s: string): string {
  return s.replace(/'/g, "''");
}

export async function listUserGroups(): Promise<UserGroupRow[]> {
  const r = await adminRpc.query(`
    SELECT uid, name, COALESCE(email,'') AS email,
           COALESCE(description,'') AS description,
           COALESCE(handle,'') AS handle,
           status, show_as_user, show_as_group,
           COALESCE(extra_data, '{}'::jsonb) AS extra_data
      FROM usergroup
     WHERE status = 'active'
     ORDER BY name
  `);
  return r.rows as unknown as UserGroupRow[];
}

export async function listMemberships(): Promise<MembershipRow[]> {
  const r = await adminRpc.query(`
    SELECT uid, memberuid, groupuid, status
      FROM groupmembership
     WHERE status = 'active'
  `);
  return r.rows as unknown as MembershipRow[];
}

export async function listCredentialsForUser(
  usergroupUid: string,
): Promise<CredentialRow[]> {
  const r = await adminRpc.query(`
    SELECT uid, usergroupuid, provider, lookupcode,
           COALESCE(rawdata,'') AS rawdata,
           extra_data, status
      FROM usercredentials
     WHERE usergroupuid = '${esc(usergroupUid)}' AND status = 'active'
     ORDER BY provider, lookupcode
  `);
  return r.rows as unknown as CredentialRow[];
}

export async function listUncorrelatedCredentials(): Promise<CredentialRow[]> {
  const r = await adminRpc.query(`
    SELECT uid, usergroupuid, provider, lookupcode,
           COALESCE(rawdata,'') AS rawdata,
           extra_data, status
      FROM usercredentials
     WHERE usergroupuid IS NULL AND status = 'active'
     ORDER BY provider, lookupcode
  `);
  return r.rows as unknown as CredentialRow[];
}

// listPromotableWorkerCredentials returns credentials that are already
// bound to a usergroup (a person who already has access), declared
// kind=worker, but have NO continuum.worker row for that usergroup. This
// is the "turn a person into a worker" case: they ran
// request-worker-access on their own key, so the credential is already
// theirs — it just needs the server/worker rows provisioned. These do
// NOT show up in listUncorrelatedCredentials (usergroupuid is set), so
// the pending page merges this list in to surface a "Promote to worker"
// action — mirroring the CLI's workerCount==0 re-provision branch.
export async function listPromotableWorkerCredentials(): Promise<
  CredentialRow[]
> {
  const r = await adminRpc.query(`
    SELECT c.uid, c.usergroupuid, c.provider, c.lookupcode,
           COALESCE(c.rawdata,'') AS rawdata,
           c.extra_data, c.status
      FROM usercredentials c
     WHERE c.usergroupuid IS NOT NULL
       AND c.status = 'active'
       AND c.extra_data->'requestAccess'->>'kind' = 'worker'
       AND NOT EXISTS (
         SELECT 1 FROM worker w WHERE w.usergroup_uid = c.usergroupuid
       )
     ORDER BY c.provider, c.lookupcode
  `);
  return r.rows as unknown as CredentialRow[];
}

export async function correlateCredential(
  credentialUid: string,
  usergroupUid: string,
): Promise<void> {
  const resp = await adminRpc.update(
    "usercredentials",
    { uid: credentialUid },
    { usergroupuid: usergroupUid },
  );
  if (!resp.success) {
    throw new Error(resp.error?.message || "correlate failed");
  }
}

export async function deleteCredential(credentialUid: string): Promise<void> {
  const resp = await adminRpc.delete("usercredentials", { uid: credentialUid });
  if (!resp.success) {
    throw new Error(resp.error?.message || "delete failed");
  }
}

export async function addMembership(
  uid: string,
  memberUid: string,
  groupUid: string,
): Promise<void> {
  const resp = await adminRpc.insert("groupmembership", {
    uid,
    memberuid: memberUid,
    groupuid: groupUid,
  });
  if (!resp.success) {
    throw new Error(resp.error?.message || "add membership failed");
  }
}

export async function deleteMembership(membershipUid: string): Promise<void> {
  const resp = await adminRpc.delete("groupmembership", { uid: membershipUid });
  if (!resp.success) {
    throw new Error(resp.error?.message || "delete membership failed");
  }
}

// createUserGroup accepts the underlying physical fields only. The display
// lens (show_as_user / show_as_group) is calculated server-side from
// `email`, credential presence, and the optional extra_data.show_as
// override — see the qubes firewall model.
//
// extraconfig (optional) is written verbatim into the row's extra_data
// jsonb. Callers that want to stamp a show_as override at create time
// (e.g. the pending-credentials approval flow always creates rows that
// display as a user) pass {show_as: "user"} here.
export async function createUserGroup(
  uid: string,
  name: string,
  email: string,
  description: string,
  extraconfig?: Record<string, unknown>,
  handle?: string,
): Promise<void> {
  // description is not null; a row written without one is described by its
  // name (godev migrations/20260930-usergroup-description-required.sql).
  const values: DbRow = {
    uid,
    name,
    description: description || name,
  };
  if (email) values.email = email;
  if (handle) values.handle = handle;
  if (extraconfig && Object.keys(extraconfig).length > 0) {
    values.extra_data = JSON.stringify(extraconfig);
  }
  const resp = await adminRpc.insert("usergroup", values);
  if (!resp.success) {
    throw new Error(resp.error?.message || "create usergroup failed");
  }
}

// A handle is a person's short, permanent name: lowercase letters and digits, an inner "-" allowed,
// starting with a letter (glen, robbi, ron-k). handleFromEmail is the suggestion a form starts from.
export const HANDLE_PATTERN = /^[a-z][a-z0-9-]*[a-z0-9]$|^[a-z]$/;

export function handleProblem(handle: string): string | null {
  if (handle === "") return null;
  return HANDLE_PATTERN.test(handle)
    ? null
    : "a handle is lowercase letters and digits, may have an inner -, and starts with a letter";
}

export function handleFromEmail(email: string): string {
  const local = email.split("@")[0] ?? "";
  return local.toLowerCase().replace(/[^a-z0-9-]/g, "").replace(/^[^a-z]+/, "").replace(/-+$/, "");
}

export async function updateUserGroup(
  uid: string,
  patch: Partial<Pick<UserGroupRow, "email" | "description" | "handle">>,
): Promise<void> {
  const resp = await adminRpc.update("usergroup", { uid }, patch as DbRow);
  if (!resp.success) {
    throw new Error(resp.error?.message || "update usergroup failed");
  }
}

// readUserGroupExtraconfig returns the current parsed extra_data
// object for a single usergroup row. The RPC may serialize JSONB as a
// JSON string or as an object depending on the rpc layer — normalize
// to object here.
function readExtraconfig(raw: unknown): Record<string, unknown> {
  if (raw == null) return {};
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === "object") return { ...raw };
  return {};
}

// ServerRow / WorkerRow are minimal projections of server and
// worker. We touch these tables only during the
// "ContinuumWorker" persona approval to pre-create the server +
// worker records and link the worker's extra_data.usergroupuid to
// the new user, so the remote box can immediately resolve its
// worker on first contact.
export interface ServerRow {
  uid: string;
  name: string;
}

export interface WorkerRow {
  uid: string;
  serveruid: string;
  login: string;
  extra_data: unknown;
}

export async function findServerByName(name: string): Promise<ServerRow | null> {
  const r = await adminRpc.query(`
    SELECT uid, name FROM server
     WHERE name = '${esc(name)}'
     LIMIT 1
  `);
  if (r.rows.length === 0) return null;
  return r.rows[0] as unknown as ServerRow;
}

export async function listServers(): Promise<ServerRow[]> {
  const r = await adminRpc.query(`
    SELECT uid, name
      FROM server
     ORDER BY name
  `);
  return r.rows as unknown as ServerRow[];
}

// insertServer creates a new server row. The optional fields
// let the caller stamp the row at create time rather than needing a
// follow-up update — important for vpnDomainName, which the
// supervisor service-action path requires set or it refuses to run
// (hermes/hermes-main.go: "The {server} server has no vpnDomainName
// configured"). fingerprint is JSON-stringified before insert,
// matching the pattern used by extra_data writes elsewhere.
export async function insertServer(
  uid: string,
  name: string,
  opts?: {
    vpnDomainName?: string;
    hostServerUid?: string;
    fingerprint?: object;
  },
): Promise<void> {
  const values: DbRow = {
    uid,
    name,
    description: `Created via admin pending-credentials approval`,
  };
  if (opts?.vpnDomainName) values.vpndomainname = opts.vpnDomainName;
  if (opts?.hostServerUid) values.hostserveruid = opts.hostServerUid;
  if (opts?.fingerprint && Object.keys(opts.fingerprint).length > 0) {
    values.fingerprint = JSON.stringify(opts.fingerprint);
  }
  const resp = await adminRpc.insert("server", values);
  if (!resp.success) {
    throw new Error(resp.error?.message || "insert server failed");
  }
}

export async function findWorkerByServerLogin(
  serverUid: string,
  login: string,
): Promise<WorkerRow | null> {
  const r = await adminRpc.query(`
    SELECT uid, serveruid, login, COALESCE(extra_data, '{}'::jsonb) AS extra_data
      FROM worker
     WHERE serveruid = '${esc(serverUid)}' AND login = '${esc(login)}'
     LIMIT 1
  `);
  if (r.rows.length === 0) return null;
  return r.rows[0] as unknown as WorkerRow;
}

// insertWorker creates a new worker row. usergroup_uid is a
// typed column on the worker table (replacing the prior
// extra_data.usergroupuid JSON shoehorn) so the lookups in
// continuum.go can use the proper Where clause. linuxUid (optional) is
// the submitter's getuid(2) captured at request-access time.
export async function insertWorker(
  uid: string,
  serverUid: string,
  login: string,
  hostname: string,
  usergroupUid: string,
  linuxUid?: number,
): Promise<void> {
  const values: Record<string, unknown> = {
    uid,
    serveruid: serverUid,
    login,
    usergroup_uid: usergroupUid,
    description: `Auto-created worker for ${login}@${hostname}`,
  };
  if (typeof linuxUid === "number") values.linux_uid = linuxUid;
  const resp = await adminRpc.insert("worker", values);
  if (!resp.success) {
    throw new Error(resp.error?.message || "insert worker failed");
  }
}

// linkWorkerToUsergroup updates an existing worker row's
// usergroup_uid column. linuxUid (optional) is stamped alongside when
// the submitter captured one. Used during re-approval of a credential
// that targets a worker row already created by a prior approval.
export async function linkWorkerToUsergroup(
  workerUid: string,
  usergroupUid: string,
  linuxUid?: number,
): Promise<void> {
  const values: Record<string, unknown> = { usergroup_uid: usergroupUid };
  if (typeof linuxUid === "number") values.linux_uid = linuxUid;
  const resp = await adminRpc.update("worker", { uid: workerUid }, values);
  if (!resp.success) {
    throw new Error(resp.error?.message || "update worker failed");
  }
}

// ---------------------------------------------------------------------------
// System monitoring enrollment: resolve the platform checker fileset, then
// create a Monitor + a periodic Schedule for a freshly-approved worker. These
// mirror insertServer/insertWorker (dbaccess.v1.Insert via adminRpc). The
// "system-healthchecks" fileset is seeded at bootstrap-db time and carries the monitor plugins; the
// Monitor points at its systemMetricsMonitor plugin, and the job runs that plugin's collect.

export interface SystemFilesetRef {
  filesetUid: string;
  revisionUid: string;
}

// resolveSystemFileset finds the bootstrap-seeded "system-healthchecks" fileset
// and its current revision. Throws if absent (bootstrap-db didn't seed it).
export async function resolveSystemFileset(): Promise<SystemFilesetRef> {
  const r = await adminRpc.query(`
    SELECT uid, COALESCE(current_revision,'') AS current_revision
      FROM fileset
     WHERE name = 'system-healthchecks'
     LIMIT 1
  `);
  if (r.rows.length === 0) {
    throw new Error(
      "system-healthchecks fileset not found — was the database bootstrapped with the system-fileset phase?",
    );
  }
  const row = r.rows[0] as { uid: string; current_revision: string };
  if (!row.current_revision) {
    throw new Error("system-healthchecks fileset has no current revision");
  }
  return { filesetUid: row.uid, revisionUid: row.current_revision };
}

// AlertChannelRow is an enabled continuum.alertchannel (an alert destination).
export interface AlertChannelRow {
  uid: string;
  name: string;
  kind: string;
}

// listAlertChannels returns the enabled alert channels, by name. Used to pick a
// monitor's alert destination at creation time.
export async function listAlertChannels(): Promise<AlertChannelRow[]> {
  const r = await adminRpc.query(
    `SELECT uid, name, kind FROM alertchannel WHERE enabled = true ORDER BY name`,
  );
  return r.rows as unknown as AlertChannelRow[];
}

// defaultChannelUids resolves the channel(s) a NEW monitor should alert to when
// the operator hasn't explicitly chosen: exactly one enabled channel => use it
// (so a monitor created with a single default channel actually alerts); zero or
// many => empty (zero: nothing to alert to; many: the UI must prompt, so we do
// not guess). Returns a uid array suitable for channel_uids.
export async function defaultChannelUids(): Promise<string[]> {
  const channels = await listAlertChannels();
  return channels.length === 1 ? [channels[0].uid] : [];
}

// insertMonitor creates a continuum.monitor that names the system metrics plugin
// (systemMetricsMonitor of the system fileset; FEATURE-20261025-unify-the-checker-pattern). Defaults: enabled, built-in
// thresholds (empty extra_data.thresholds => conventions apply). When the caller
// does not specify channelUids, the channel is resolved via defaultChannelUids
// (auto-use the single channel; empty when 0 or >1 — the caller/UI is
// responsible for prompting when there is more than one). Returns the monitor uid.
export async function insertMonitor(
  uid: string,
  pluginFilesetUid: string,
  opts?: { pluginName?: string; channelUids?: string[]; thresholds?: unknown[] },
): Promise<void> {
  const channelUids = opts?.channelUids ?? (await defaultChannelUids());
  const values: DbRow = {
    uid,
    enabled: true,
    plugin_fileset_uid: pluginFilesetUid,
    plugin_name: opts?.pluginName ?? "systemMetricsMonitor",
    channel_uids: JSON.stringify(channelUids),
    extra_data: JSON.stringify({ config: {}, thresholds: opts?.thresholds ?? [] }),
  };
  const resp = await adminRpc.insert("monitor", values);
  if (!resp.success) {
    throw new Error(resp.error?.message || "insert monitor failed");
  }
}

// MonitorStatusRow is one (monitor, schedule, breaker) triple for the Monitors
// health dashboard. A monitor with no linked schedule yields one row with null
// schedule/breaker fields; a monitor driving several schedules yields one row
// each (so the breaker status is per-schedule, which is how the breaker is keyed).
export interface MonitorStatusRow {
  monitor_uid: string;
  enabled: boolean;
  plugin_name: string | null;
  plugin_fileset_uid: string | null;
  expected_interval_seconds: number | null;
  job_name: string | null; // every monitor has a job; not every job has a schedule
  schedule_uid: string | null;
  schedule_name: string | null;
  schedule_enabled: boolean | null;
  breaker_status: string | null; // "green" | "yellow" | "red" | null (no breaker yet)
  consecutive_failures: number | null;
  opened_at: string | null;
  last_alert_at: string | null;
  manual_override: string | null;
  // the monitor's extra_data: its plugin's config (extra_data.config) and thresholds
  extra_data?: unknown;
}

// listMonitorsWithStatus returns every monitor joined to the job(s) it drives, each
// job's schedule(s), and the job's circuit-breaker status — the data behind the
// Monitors health dashboard. The monitor + breaker live on the JOB now (the breaker
// is per-job, so all schedules of a job share one breaker); a job's schedules are its
// timing triggers. LEFT JOINs so a monitor with no job, or a job with no schedule
// (a supervised/daemon job) or no breaker yet, still appears as a row.
export async function listMonitorsWithStatus(): Promise<MonitorStatusRow[]> {
  const r = await adminRpc.query(`
    SELECT m.uid                        AS monitor_uid,
           m.enabled                    AS enabled,
           m.plugin_name                AS plugin_name,
           m.plugin_fileset_uid         AS plugin_fileset_uid,
           m.extra_data                 AS extra_data,
           m.expected_interval_seconds  AS expected_interval_seconds,
           j.name                       AS job_name,
           s.uid                        AS schedule_uid,
           s.name                       AS schedule_name,
           s.enabled                    AS schedule_enabled,
           cb.breaker_status            AS breaker_status,
           cb.consecutive_failures      AS consecutive_failures,
           cb.opened_at                 AS opened_at,
           cb.last_alert_at             AS last_alert_at,
           cb.manual_override           AS manual_override
      FROM monitor m
      LEFT JOIN job j             ON j.monitor_uid = m.uid
      LEFT JOIN job_schedule s    ON s.job_uid = j.uid
      LEFT JOIN circuitbreaker cb ON cb.job_uid = j.uid
     ORDER BY j.name, s.name
  `);
  return r.rows as unknown as MonitorStatusRow[];
}

// MonitorDetail is one monitor's full policy row (the config the detail page shows).
export interface MonitorDetail {
  uid: string;
  job_name: string | null; // name of the job this monitor drives — the monitor's natural title
  enabled: boolean;
  expected_interval_seconds: number | null;
  grace_seconds: number;
  runtime_yellow_seconds: number | null;
  runtime_red_seconds: number | null;
  alert_on_nonzero_exit: boolean;
  alert_on_stderr: boolean;
  alert_on_overdue: boolean;
  channel_uids: unknown; // JSON array of AlertChannel uids
  plugin_fileset_uid: string | null;
  plugin_name: string | null;
  extra_data: unknown; // { config: {...}, thresholds: [{key, scope, yellow, red}], ... }
  created_at: string | null;
  updated_at: string | null;
}

// MonitorScheduleRow is one schedule driven by a monitor, with its breaker status —
// the "linked schedules" section of the detail page.
export interface MonitorScheduleRow {
  schedule_uid: string;
  schedule_name: string;
  schedule_enabled: boolean;
  schedule_expr: string | null;
  breaker_status: string | null;
  consecutive_failures: number | null;
  opened_at: string | null;
  last_alert_at: string | null;
  manual_override: string | null;
}

// getMonitor fetches one monitor's full policy row by uid, or null if not found.
export async function getMonitor(uid: string): Promise<MonitorDetail | null> {
  const r = await adminRpc.query(`
    SELECT m.uid, m.enabled, m.expected_interval_seconds, m.grace_seconds, m.runtime_yellow_seconds, m.runtime_red_seconds,
           m.alert_on_nonzero_exit, m.alert_on_stderr, m.alert_on_overdue,
           COALESCE(m.channel_uids, '[]'::jsonb) AS channel_uids,
           m.plugin_fileset_uid, m.plugin_name,
           COALESCE(m.extra_data, '{}'::jsonb)   AS extra_data,
           m.created_at, m.updated_at,
           (SELECT j.name FROM job j
             WHERE j.monitor_uid = m.uid
             ORDER BY j.name LIMIT 1)            AS job_name
      FROM monitor m
     WHERE m.uid = '${esc(uid)}'
  `);
  return (r.rows[0] as unknown as MonitorDetail) ?? null;
}

// listMonitorSchedules returns the schedules of the job(s) this monitor drives, plus
// the job's breaker status. The monitor lives on the job, so we match schedules whose
// job has this monitor; the breaker is per-job (cb.job_uid), so every schedule of a
// job shows that one job-level breaker status.
export async function listMonitorSchedules(monitorUid: string): Promise<MonitorScheduleRow[]> {
  const r = await adminRpc.query(`
    SELECT s.uid                   AS schedule_uid,
           s.name                  AS schedule_name,
           s.enabled               AS schedule_enabled,
           s.schedule_expr         AS schedule_expr,
           cb.breaker_status       AS breaker_status,
           cb.consecutive_failures AS consecutive_failures,
           cb.opened_at            AS opened_at,
           cb.last_alert_at        AS last_alert_at,
           cb.manual_override      AS manual_override
      FROM job_schedule s
      JOIN job j                  ON j.uid = s.job_uid
      LEFT JOIN circuitbreaker cb ON cb.job_uid = j.uid
     WHERE j.monitor_uid = '${esc(monitorUid)}'
     ORDER BY s.name
  `);
  return r.rows as unknown as MonitorScheduleRow[];
}

// MonitorDecisionRow is one logged DECISION (continuum.circuitbreaker_event) for a unit driven by this
// monitor — the full input -> output of one checker evaluation. A row is written every real evaluation
// (green/yellow/red, transition or steady), so the list is the unit's decision history: from_status ->
// to_status (equal when steady), the worst-of reason, and the JSON drill-in — input_event (the readings/
// thresholds/breaker the checker SAW), check_results (every check's value/bounds/level + which DROVE it),
// and actions (what the host did). This is the "drill into a red/yellow and see what turned it that color".
export interface MonitorDecisionRow {
  uid: string;
  job_uid: string | null;
  from_status: string | null;
  to_status: string;
  event_class: string | null;
  event_kind: string | null;
  reason: string | null;
  input_event: unknown; // { readings:[{key,scope,value}], thresholds, breaker:{status,...}, nowMillis, ... }
  check_results: Array<{
    key?: string;
    scope?: string;
    value?: number;
    unit?: string;
    yellowBound?: number;
    redBound?: number;
    level?: string;
    reason?: string;
    isDriver?: boolean;
  }>;
  actions: Array<{ kind?: string; status?: string; fromStatus?: string; toStatus?: string; transition?: string; reason?: string }>;
  created_at: string;
}

// listMonitorDecisions returns the recent decision-log rows for every job this monitor drives, newest
// first. The breaker (and its decision log) keys on job_uid; the monitor lives on the job, so we match
// decisions whose job has this monitor. limit bounds the history pulled (default 100).
export async function listMonitorDecisions(monitorUid: string, limit = 100): Promise<MonitorDecisionRow[]> {
  const r = await adminRpc.query(`
    SELECT cbe.uid, cbe.job_uid,
           cbe.from_status, cbe.to_status, cbe.event_class, cbe.event_kind, cbe.reason,
           COALESCE(cbe.input_event, '{}'::jsonb)   AS input_event,
           COALESCE(cbe.check_results, '[]'::jsonb) AS check_results,
           COALESCE(cbe.actions, '[]'::jsonb)       AS actions,
           cbe.created_at
      FROM circuitbreaker_event cbe
     WHERE cbe.job_uid IN (SELECT uid FROM job WHERE monitor_uid = '${esc(monitorUid)}')
     ORDER BY cbe.created_at DESC
     LIMIT ${Number.isFinite(limit) ? Math.max(1, Math.floor(limit)) : 100}
  `);
  // dbaccess.v1.Query returns jsonb columns inconsistently — already-parsed, a JSON string, or even a
  // DOUBLE-encoded JSON string. asJson/asJsonArray normalize all of these to real objects/arrays.
  return (r.rows as Record<string, unknown>[]).map((row) => ({
    uid: String(row.uid ?? ""),
    job_uid: (row.job_uid as string | null) ?? null,
    from_status: (row.from_status as string | null) ?? null,
    to_status: String(row.to_status ?? ""),
    event_class: (row.event_class as string | null) ?? null,
    event_kind: (row.event_kind as string | null) ?? null,
    reason: (row.reason as string | null) ?? null,
    input_event: asJson(row.input_event),
    check_results: asJsonArray(row.check_results) as MonitorDecisionRow["check_results"],
    actions: asJsonArray(row.actions) as MonitorDecisionRow["actions"],
    created_at: String(row.created_at ?? ""),
  }));
}

// asJson / asJsonArray tolerate a jsonb column arriving either already-parsed (object/array) or as a JSON
// string (what dbaccess.v1.Query returns for jsonb) — returning a usable value the UI can render.
function asJson(v: unknown): unknown {
  // dbaccess.v1.Query can return a jsonb cell already-parsed (object/array) OR as a JSON string, and in some
  // cases DOUBLE-encoded (a JSON string whose content is itself a JSON string). Parse repeatedly until the
  // value is no longer a string (bounded so a genuine string scalar can't loop).
  let cur = v;
  for (let i = 0; i < 3 && typeof cur === "string"; i++) {
    try {
      cur = JSON.parse(cur);
    } catch {
      break;
    }
  }
  return cur ?? {};
}
function asJsonArray(v: unknown): unknown[] {
  const parsed = asJson(v);
  return Array.isArray(parsed) ? parsed : [];
}

// --- monitor metrics (continuum.metrics, category per kind) --------------------------------------
// Every active collector writes ONE metrics row per probe: { readings: [{key,scope,value,unit}] },
// keyed by job_uid, tagged with the kind's category (a monitor plugin's kind: 'ping', 'cdc', 'metric'). That's a
// denser, purpose-built time-series than the per-decision circuitbreaker_event, so the monitor visuals
// (list signal, detail charts, widget) read readings from metrics; the breaker stays the red/yellow/green
// STATUS source. The functions below are category-parameterized so every kind shares one read path.

// LatestPingRow is the most recent ping probe for one monitor (its latest readings + when).
export interface LatestPingRow {
  monitor_uid: string;
  job_uid: string;
  created_at: string;
  readings: unknown; // { readings: [{key,scope,value}] } — normalized by the caller (parseReadings)
}

// latestReadings returns the most recent metrics row for each of the given monitor uids in the given
// category (one row per job, the freshest), so the monitor list can show each monitor's current readings
// inline without a per-row query. `category` selects the kind's metrics stream (the plugin's kind:
// 'ping', 'cdc', 'metric'). Empty input -> empty map. Keyed by monitor_uid.
//
// `keys` narrows the readings to those `key` values, IN THE DATABASE. Pass the caller's signal keys:
// the Signal column renders a fixed handful per kind, while a system metrics capture carries 29-146
// readings (one `disk` per filesystem, and so on). Unfiltered, the 21 rows behind that column shipped
// 1,506 readings — 163 KB on the wire — to render ~40 chips
// (TASK-20260806-monitor-trim-readings-payload-and-paint-early). Omit `keys` to get everything.
export async function latestReadings(
  monitorUids: string[],
  category = "ping",
  keys?: string[],
): Promise<Map<string, LatestPingRow>> {
  const out = new Map<string, LatestPingRow>();
  const uids = monitorUids.filter((u) => u);
  if (uids.length === 0) return out;
  const inList = uids.map((u) => `'${esc(u)}'`).join(", ");
  const cat = esc(category);
  // Filter the readings array server-side when the caller named its keys. jsonb_agg over
  // jsonb_array_elements is accepted by the firewall's restricted parser (verified against the real
  // dbaccess.v1.Query path). This correlated subquery is fine — unlike the one this function used to
  // carry, it runs over ONE row's small array rather than driving a scan of the whole category.
  // jsonb_agg returns NULL when nothing matches, hence the COALESCE to an empty array; parseReadings
  // accepts a bare array as readily as {readings: [...]}, so the client needs no change either way.
  const wantedKeys = (keys ?? []).filter((k) => k);
  const readingsExpr = wantedKeys.length
    ? `COALESCE((SELECT jsonb_agg(e) FROM jsonb_array_elements(m.data->'readings') e
                  WHERE e->>'key' IN (${wantedKeys.map((k) => `'${esc(k)}'`).join(", ")})), '[]'::jsonb)`
    : `COALESCE(m.data, '{}'::jsonb)`;
  // Keep the latest row per job by matching created_at against MAX(created_at) for that JOB — note
  // the correlation is to `j.uid`, the 30-row job table, NOT to `m.job_uid`. That distinction is the
  // whole performance story here, and the two forms are semantically identical (m.job_uid = j.uid is
  // the join condition), so it is easy to "simplify" one into the other and silently lose 1000x.
  //
  //   - correlated to m.job_uid: Postgres cannot push the job restriction under the correlation, so
  //     it scans EVERY row of the category and runs one MAX probe per row — 153,639 scanned and
  //     153,639 probes to return 21 rows. 2536ms.
  //   - correlated to j.uid: the subquery depends only on the job, so it runs once per job and
  //     collapses into an index-backward-scan on (job_uid, category, created_at). 2.9ms.
  //
  // That index is what makes this form viable and it is NOT optional — without it the per-job probe
  // has nothing to seek on and filters category row by row. It ships with the schema
  // (Metrics.cube.zoo, @sqlIndexes) and was applied to the live envs by
  // migrations/20260806-metrics-job-category-created-at-index.sql in godev.
  // Ticket: TASK-20260806-metrics-job-category-index.
  //
  // Rejected alternatives, both measured on prod against the same 30-monitor set:
  //   - DISTINCT ON — the natural Postgres answer; the firewall's restricted SQL parser rejects it.
  //   - CROSS JOIN LATERAL ... ORDER BY created_at DESC LIMIT 1 — 5.2ms with the index, but the same
  //     parser rejects it too, and its Apply node prints the keywords verbatim for SQL Server, which
  //     Postgres has no syntax for.
  //   - derived table + GROUP BY — what this replaced. Correct and index-aware, but GROUP BY MAX
  //     still walks every row of each group (Postgres has no skip scan), so it read 154,373 rows to
  //     return 21: 406ms before the index, 297ms after.
  const r = await adminRpc.query(`
    SELECT j.monitor_uid                       AS monitor_uid,
           m.job_uid                           AS job_uid,
           m.created_at                        AS created_at,
           ${readingsExpr}                     AS readings
      FROM job j
      JOIN metrics m ON m.job_uid = j.uid AND m.category = '${cat}'
     WHERE j.monitor_uid IN (${inList})
       AND m.created_at = (
         SELECT MAX(m2.created_at) FROM metrics m2
          WHERE m2.job_uid = j.uid AND m2.category = '${cat}'
       )
  `);
  for (const row of r.rows as Record<string, unknown>[]) {
    const monitorUid = String(row.monitor_uid ?? "");
    if (!monitorUid) continue;
    out.set(monitorUid, {
      monitor_uid: monitorUid,
      job_uid: String(row.job_uid ?? ""),
      created_at: String(row.created_at ?? ""),
      readings: asJson(row.readings),
    });
  }
  return out;
}

// MonitorMetricRow is one metrics row of a monitor's job, whatever its category: what one pass wrote.
export interface MonitorMetricRow {
  job_uid: string;
  category: string;
  created_at: string;
  readings: unknown; // { readings: [{key,scope,value,unit}] }
}

// monitorJobUids is the jobs a monitor drives.
export async function monitorJobUids(monitorUid: string): Promise<string[]> {
  const j = await adminRpc.query(`SELECT uid FROM job WHERE monitor_uid = '${esc(monitorUid)}'`);
  return (j.rows as Record<string, unknown>[]).map((row) => String(row.uid ?? "")).filter((uid) => uid !== "");
}

// monitorMetrics is the metrics rows of the given jobs, newest first: the newest `limit`, or those written
// since `sinceIso`. Every category, since a generic page does not know the kind's. One ordered leg per job,
// so each leg is answered from the (job_uid, created_at) index: a single query over the job set sorts the
// whole matching history before it limits.
export async function monitorMetrics(
  jobUids: string[],
  opts: { limit: number; sinceIso?: string },
): Promise<MonitorMetricRow[]> {
  if (jobUids.length === 0) return [];
  const n = Number.isFinite(opts.limit) ? Math.max(1, Math.floor(opts.limit)) : 1;
  const since = opts.sinceIso ? `AND m.created_at > '${esc(opts.sinceIso)}'` : "";
  const legs = jobUids
    .map(
      (uid) => `
      (SELECT m.job_uid, m.category, m.created_at, COALESCE(m.data, '{}'::jsonb) AS readings
         FROM metrics m
        WHERE m.job_uid = '${esc(uid)}' ${since}
        ORDER BY m.created_at DESC
        LIMIT ${n})`,
    )
    .join("\n      UNION ALL");
  const r = await adminRpc.query(`
    SELECT u.job_uid, u.category, u.created_at, u.readings
      FROM (${legs}
      ) u
     ORDER BY u.created_at DESC
     LIMIT ${n}
  `);
  return (r.rows as Record<string, unknown>[]).map((row) => ({
    job_uid: String(row.job_uid ?? ""),
    category: String(row.category ?? ""),
    created_at: String(row.created_at ?? ""),
    readings: asJson(row.readings),
  }));
}

// insertSchedule enrolls the system-metrics job: it creates the JOB (which carries the worker, the
// run config, and the monitor) and a timing-only SCHEDULE that fires it. `uid` is the schedule uid;
// the job gets its own uid. schedule_expr is a gocron expression (default "every 5 minutes").
export async function insertSchedule(
  uid: string,
  name: string,
  workerUid: string,
  monitorUid: string,
  filesetUid: string,
  opts?: { scheduleExpr?: string },
): Promise<void> {
  // The JOB owns the worker, fileset, payload, and monitor (monitoring is per-job now).
  const jobUid = uuidv7().replaceAll("-", "");
  const jobValues: DbRow = {
    uid: jobUid,
    name,
    workerUid,
    workerEnabled: true,
    kind: "scheduled",
    description: "System metrics monitor (created via admin approval)",
    concurrency_policy: "overlap",
    monitor_uid: monitorUid,
    fileset_uid: filesetUid,
    // The job runs the monitor plugin's collect (systemMetricsMonitor): `a8 checkpoint exec --job-event`
    // captures the host's metrics through core.sys and writes the run's metrics row. runDirect: the leaf
    // reports its own processrun, as every checkpoint job does.
    payload_json: JSON.stringify({
      jobName: name,
      command: ["a8", "checkpoint", "exec", "--job-event"],
      runDirect: true,
      captureConsole: false,
    }),
    max_attempts: 25,
  };
  const jobResp = await adminRpc.insert("job", jobValues);
  if (!jobResp.success) {
    throw new Error(jobResp.error?.message || "insert job failed");
  }

  // The SCHEDULE is timing-only and points at the job.
  const scheduleResp = await adminRpc.insert("job_schedule", {
    uid,
    name,
    description: "System metrics monitor (created via admin approval)",
    job_uid: jobUid,
    schedule_expr: opts?.scheduleExpr ?? "every 5 minutes",
    enabled: true,
  });
  if (!scheduleResp.success) {
    throw new Error(scheduleResp.error?.message || "insert schedule failed");
  }
}

// setShowAsOverride writes the operator's display-lens choice to
// usergroup.extra_data.show_as.
//
//   value=null  → delete the key (natural calc will apply).
//   value="user"|"group"|"both" → set the key.
//
// Read-modify-write: any other keys already in extra_data are
// preserved (the underlying dbaccess.v1.Update REPLACES the value, so
// we have to send the merged object back).
export async function setShowAsOverride(
  row: UserGroupRow,
  value: ShowAsOverride,
): Promise<void> {
  const cfg = readExtraconfig(row.extra_data);
  if (value === null) {
    delete cfg["show_as"];
  } else {
    cfg["show_as"] = value;
  }
  const resp = await adminRpc.update(
    "usergroup",
    { uid: row.uid },
    // structpb can't carry a raw nested object as a single key under
    // some serializers; encoding the object as a JSON string and
    // letting Postgres' jsonb cast handle it is the same shape used
    // by other writes (cf. usercredentials extra_data writes in the
    // hermes OAuth handler).
    { extra_data: JSON.stringify(cfg) },
  );
  if (!resp.success) {
    throw new Error(resp.error?.message || "set show_as override failed");
  }
}

